'use client';

import { useCallback, useMemo } from 'react';
import { Box, TextField, ToggleButtonGroup, ToggleButton, Tooltip, Pagination, Select, MenuItem, FormControl, InputLabel, Stack, Button } from '@mui/material';
import { Image as ImageIcon, Videocam, TextSnippet, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';
import { MediaType } from '@/app/features/shared/media-browser-v2/types/types';
import { debounce } from 'lodash';

const ALL_MEDIA_TYPES: MediaType[] = ['image/*', 'video/*', 'application/pdf'];

export default function MediaToolbar() {
    const { state, setSearchQuery, setMediaTypes, setMultiSelectMode } = useMediaBrowserV2();

    // Debounced search
    const debouncedSetQuery = useMemo(
        () => debounce((value: string) => {
            setSearchQuery(value);
        }, 400),
        [setSearchQuery]
    );

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSetQuery(e.target.value);
    }, [debouncedSetQuery]);

    const handleMediaTypeChange = useCallback((_: any, newTypes: MediaType[]) => {
        // Smart toggle: clicking on the only active type should activate all types
        if (state.mediaTypes.length === 1 && newTypes.length === 0) {
            setMediaTypes(ALL_MEDIA_TYPES);
            return;
        }

        // If all types are active and user clicks one, switch to only that type
        if (state.mediaTypes.length === ALL_MEDIA_TYPES.length && newTypes.length === ALL_MEDIA_TYPES.length - 1) {
            const clickedType = ALL_MEDIA_TYPES.find(t => !newTypes.includes(t));
            if (clickedType) {
                setMediaTypes([clickedType]);
                return;
            }
        }

        setMediaTypes(newTypes.length > 0 ? newTypes : ALL_MEDIA_TYPES);
    }, [state.mediaTypes, setMediaTypes]);

    const handleToggleMultiSelect = useCallback(() => {
        setMultiSelectMode(!state.multiSelectMode);
    }, [state.multiSelectMode, setMultiSelectMode]);

    return (
        <Box>
            {/* Search & Filters */}
            <Stack direction="row" spacing={2} alignItems="center" className="min-w-0">

                <TextField
                    placeholder="Medien suchen..."
                    defaultValue={state.searchQuery}
                    onChange={handleSearchChange}
                    size="small"
                    className="flex-1"
                />

                <ToggleButtonGroup
                    value={state.mediaTypes}
                    onChange={handleMediaTypeChange}
                    size="small"
                >
                    <Tooltip title="Bilder">
                        <ToggleButton value="image/*">
                            <ImageIcon />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Videos">
                        <ToggleButton value="video/*">
                            <Videocam />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="PDFs">
                        <ToggleButton value="application/pdf">
                            <TextSnippet />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>

                <Tooltip title="Mehrfachauswahl">
                    <Button
                        onClick={handleToggleMultiSelect}
                        variant={state.multiSelectMode ? "contained" : "outlined"}
                        color="primary"
                        startIcon={state.multiSelectMode ? <CheckBox /> : <CheckBoxOutlineBlank />}
                    >
                        Mehrfachauswahl
                    </Button>
                </Tooltip>
            </Stack>
        </Box>
    );
}

// Separate Pagination component to be used below the grid
export function MediaPagination() {
    const { state, setPage, setPageSize } = useMediaBrowserV2();

    const handlePageChange = useCallback((_: any, page: number) => {
        setPage(page);
    }, [setPage]);

    const handlePageSizeChange = useCallback((e: any) => {
        setPageSize(e.target.value);
    }, [setPageSize]);

    if (state.totalPages <= 1) {
        return null;
    }

    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            className="pt-4"
            sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
        >
            <Box className="flex items-center gap-4">
                <Pagination
                    count={state.totalPages}
                    page={state.page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                />
            </Box>

            <FormControl size="small" className="min-w-[120px]">
                <InputLabel>Pro Seite</InputLabel>
                <Select
                    value={state.pageSize}
                    label="Pro Seite"
                    onChange={handlePageSizeChange}
                >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );
}
